import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, downloadFile, gbp } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";
import { BTN } from "./AdminInvoices";
import { EMPTY_PURCHASE, PurchaseForm } from "./PurchaseForm";

const download = (no, fmt) => downloadFile(`/admin/purchases/${no}/${fmt}`, `Cirencester-4x4-Purchase-Agreement-${no}.${fmt}`).catch((e) => toast.error(apiError(e)));

const BlankAgreement = () => (
  <section className="mt-8 border border-stone bg-paper p-6">
    <h2 className="mb-4 font-serif text-2xl">Blank Vehicle Purchase Agreement</h2>
    <div className="flex flex-wrap items-center justify-between gap-4 border border-stone p-4" data-testid="doc-purchase-agreement">
      <p className="max-w-2xl text-sm text-charcoal/75">For buying a vehicle from a private or trade seller. Includes seller declarations, HPI and finance protections, a 14-day comeback and a handover record. Print it blank to complete by hand.</p>
      <div className="flex gap-2">
        {[["Word", "docx"], ["PDF", "pdf"]].map(([l, ext]) => (
          <a key={ext} href={`/docs/Cirencester-4x4-Vehicle-Purchase-Agreement.${ext}`} download data-testid={`doc-purchase-agreement-${ext}`} className={BTN}>Download {l}</a>
        ))}
      </div>
    </div>
    <p className="mt-4 text-sm text-charcoal/75">Sellers' valuation requests are under <Link to="/admin/valuations" className="underline" data-testid="purchases-valuations-link">Valuations</Link>.</p>
  </section>
);

const PurchaseRow = ({ p, onEdit }) => (
  <li className={`flex flex-wrap items-center justify-between gap-3 border-b border-stone py-4 ${p.status === "Cancelled" ? "opacity-60" : ""}`} data-testid={`purchase-row-${p.agreement_no}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{p.agreement_no} <span className="font-normal text-charcoal/70">· {p.purchase_date} · {p.status}</span></p>
      <p className="text-sm">{p.seller_name} · {[p.make, p.model, p.derivative].filter(Boolean).join(" ")}{p.registration ? ` (${p.registration})` : ""}</p>
      <p className="text-sm font-semibold text-forest">{gbp(p.price)}{p.outstanding_finance ? <span className="font-normal text-charcoal/70"> · finance to settle {gbp(p.outstanding_finance)}</span> : null}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={BTN} onClick={() => download(p.agreement_no, "pdf")} data-testid={`purchase-pdf-${p.agreement_no}`}>Agreement PDF</button>
      <button type="button" className={BTN} onClick={() => download(p.agreement_no, "docx")} data-testid={`purchase-docx-${p.agreement_no}`}>Word</button>
      <button type="button" className={BTN} onClick={() => onEdit(p)} data-testid={`purchase-edit-${p.agreement_no}`}>Edit</button>
    </div>
  </li>
);

export default function AdminPurchases() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | {initial, agreementNo}
  const load = useCallback(() => {
    api.get("/admin/purchases").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const total = list.filter((p) => p.status !== "Cancelled").reduce((s, p) => s + (p.price || 0), 0);
  return (
    <div data-testid="admin-purchases">
      <AdminTitle title="Purchases">
        {!editing && <button type="button" onClick={() => setEditing({ initial: EMPTY_PURCHASE, agreementNo: null })} className="btn btn-primary !py-3" data-testid="purchase-new">Record a purchase</button>}
      </AdminTitle>
      <p className="mb-6 text-sm text-charcoal/75">Log every vehicle you buy. Each purchase gets an agreement number (PA-year-101 onwards) and a pre-filled Vehicle Purchase Agreement to print and sign. Private: only signed-in admins can see it.</p>
      {editing && (
        <div className="mb-8">
          <PurchaseForm key={editing.agreementNo || "new"} initial={editing.initial} agreementNo={editing.agreementNo}
            onSaved={(p) => { setEditing(null); load(); download(p.agreement_no, "pdf"); }} onCancel={() => setEditing(null)} />
        </div>
      )}
      <section className="border border-stone bg-paper px-6 py-2">
        {list.length ? (
          <>
            <ul>{list.map((p) => <PurchaseRow key={p.agreement_no} p={p} onEdit={(x) => { setEditing({ initial: { ...EMPTY_PURCHASE, ...x, mileage: x.mileage ?? "", outstanding_finance: x.outstanding_finance || "" }, agreementNo: x.agreement_no }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}</ul>
            <p className="py-4 text-sm text-charcoal/75" data-testid="purchases-total">{list.length} purchase{list.length === 1 ? "" : "s"} · {gbp(total)} total (excluding cancelled)</p>
          </>
        ) : <p className="py-6 text-sm text-charcoal/70" data-testid="purchases-empty">No purchases recorded yet.</p>}
      </section>
      <BlankAgreement />
    </div>
  );
}
